import '@quasar/extras/material-icons/material-icons.css'
import '@quasar/extras/material-symbols-outlined/material-symbols-outlined.css'
import '@quasar/extras/mdi-v6/mdi-v6.css'
import '@quasar/extras/fontawesome-v6/fontawesome-v6.css'
import 'quasar/src/css/index.sass'
import '../css/app.scss'
import commonComponentsMixin from './mixins/commonComponentsMixin.js'
import currentUserMixin, { syncCurrentUser } from './mixins/currentUserMixin.js'
import dialogsMixin from './mixins/dialogsMixin.js'
import fileSizeFormatMixin from './mixins/fileSizeFormatMixin.js'
import i18n from './i18n'
import navigationMixin from './mixins/navigationMixin.js'
import quasarIconSet from 'quasar/icon-set/svg-mdi-v6'
import { createApp, h } from 'vue'
import { createInertiaApp, router } from '@inertiajs/vue3'
import { Dialog, Notify, Quasar, setCssVar } from 'quasar'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { ZiggyVue } from '../../vendor/tightenco/ziggy'
import * as Sentry from '@sentry/vue'

const appName = import.meta.env.VITE_APP_NAME || 'DeBesteEHBOdoos API'
const materialSymbolNamePattern = /^[a-z][a-z0-9_]*$/
const timeoutStatuses = [408, 502, 503, 504]
const useMaterialSymbol = (iconName) => {
  if (!materialSymbolNamePattern.test(iconName) || /^(o_|r_|s_|sym_[ors]_)/.test(iconName)) {
    return undefined
  }

  return { icon: `sym_o_${iconName}` }
}
const applyTheme = (pageProps) => {
  const primaryColor = pageProps?.page?.primary_color

  if (typeof primaryColor === 'string') {
    setCssVar('primary', primaryColor)
  }
}
const notifyRequestTimeout = () => {
  Notify.create({
    message: i18n.global.t('common.errors.request_timeout_title'),
    caption: i18n.global.t('common.errors.request_timeout_message'),
    color: 'negative',
    icon: 'schedule',
    position: 'bottom',
    timeout: 10000,
  })
}
const isTimeoutMessage = (message) => {
  if (typeof message !== 'string') {
    return false
  }

  return [
    'timeout',
    'timed out',
    'upstream request timeout',
    'gateway timeout',
  ].some((needle) => message.toLowerCase().includes(needle))
}

createInertiaApp({
  title: (title) => `${title} - ${appName}`,
  resolve: (name) =>
    resolvePageComponent(
      `../vue/Pages/${name}.vue`,
      import.meta.glob('../vue/Pages/**/*.vue'),
    ),
  setup ({ el, App, props, plugin }) {
    const app = createApp({ render: () => h(App, props) })
    applyTheme(props.initialPage.props)
    syncCurrentUser(props.initialPage.props)
    router.on('success', (event) => {
      applyTheme(event.detail.page.props)
      syncCurrentUser(event.detail.page.props)
    })
    router.on('invalid', (event) => {
      const response = event.detail.response

      if (timeoutStatuses.includes(response.status)) {
        event.preventDefault()
        notifyRequestTimeout()
        Sentry.captureMessage('Inertia upstream timeout response', {
          level: 'warning',
          extra: {
            status: response.status,
            url: response.url,
          },
        })
        return
      }

      response.clone().text().then((body) => {
        if (!isTimeoutMessage(body)) {
          return
        }

        event.preventDefault()
        notifyRequestTimeout()
        Sentry.captureMessage('Inertia upstream timeout body', {
          level: 'warning',
          extra: {
            status: response.status,
            url: response.url,
          },
        })
      })
    })
    router.on('exception', (event) => {
      const exception = event.detail.exception

      if (!isTimeoutMessage(exception?.message)) {
        return
      }

      event.preventDefault()
      notifyRequestTimeout()
      Sentry.captureException(exception)
    })

    // Sentry.init({
    //   app,
    //   dsn: import.meta.env.VITE_SENTRY_DSN_PUBLIC,
    //   environment: import.meta.env.VITE_APP_ENV,
    //   // integrations: [new BrowserTracing({ tracingOrigins: ["*"] })],
    //   integrations: [
    //     Sentry.browserTracingIntegration(),
    //     Sentry.replayIntegration(),
    //   ],
    //   // Performance Monitoring
    //   tracesSampleRate: 1.0, //  Capture 100% of the transactions
    //   // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    //   tracePropagationTargets: ['*'],
    //   // Session Replay
    //   replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    //   replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    //   debug: true,
    // })

    app
      .use(plugin)
      .use(Quasar, {
        plugins: {
          Notify,
          Dialog,
        },
        iconSet: quasarIconSet,
        config: {
          iconMapFn: useMaterialSymbol,
          extras: [
            'material-icons',
            'material-symbols-outlined',
            'fontawesome-v6',
          ],
        }, // quasar config see: https://quasar.dev/start/vite-plugin/
      })
      .use(ZiggyVue)
      .use(i18n)
      .mixin(commonComponentsMixin)
      .mixin(currentUserMixin)
      .mixin(dialogsMixin)
      .mixin(fileSizeFormatMixin)
      .mixin(navigationMixin)
      .mount(el)
  },
  progress: {
    color: '#4B5563',
  },
})
